<template>
  <div class="bbe-upload-files bbe-email-attachments">
    <transition name="bbe-routing-fade">
      <span class="bbe-upload-files__error" v-if="error">{{error}}</span>
    </transition>
    <VuePerfectScrollbar class="bbe-upload-files-list">
      <transition-group tag="div" name="bbe-fade-options" class="bbe-upload-files-list__transition">
        <a class="bbe-email-attachments-file bbe-upload-files-list__container"
           @click="downloadFile($event, file)"
           :download="file.name"
           :class="{'bbe-email-attachments-file_narrow': narrow}"
           v-for="(file, index) in files" :key="file.id + ' ' + index"
           :title="file.name">
          <template v-if="isImage(file)">
            <img :src="`data:${file.type};base64,${getBase64(file)}`" class="bbe-email-attachments-file__icon"/>
          </template>
          <template v-else>
            <i class="email-icon-document bbe-email-attachments-file__icon"/>
          </template>
          <div class="bbe-email-attachments-file__name bbe-upload-files-list__name">
            {{file.name}}
          </div>
          <template v-if="file.status && file.uploaded">
            <div class="bbe-email-attachments-file__size" ref="bbeStatusUpload">
              {{convertSizeFile(file.size)}}
            </div>
          </template>
          <template v-else>
            <div class="bbe-email-attachments-file-status">
              <span class="bbe-email-attachments-file-status__progress"
                    :class="{'bbe-email-attachments-file-status__progress_infinity': !file.uploaded}"
                    ref="bbeStatusUpload"/>
            </div>
          </template>
          <i class="email-icon-close bbe-upload-files-list__close"
             @click.stop="deleteFile($event, index)"
             v-if="!disabled"/>
        </a>
      </transition-group>
    </VuePerfectScrollbar>
    <label for="files" class="bbe-upload-files-button" v-if="!disabled">
      <i class="email-icon-attach bbe-upload-files-button__icon"/>
      <input class="bbe-upload-files-button__input"
             type="file"
             id="files"
             name="files"
             multiple
             @change="getFileInput">
    </label>
  </div>
</template>

<script>
import {forEach, uniqueId, delay, findIndex} from 'lodash'
import convertSizeFile from '@/mixin/convertSizeFile'
import VuePerfectScrollbar from 'vue-perfect-scrollbar'
import UploadApi from '@/api/Upload/api'

export default {
  name: 'UploadFilesBE',
  props: ['files', 'narrow', 'dropped', 'spaces', 'disabled'],
  mixins: [convertSizeFile],
  components: {
    VuePerfectScrollbar
  },
  data () {
    return {
      error: '',
      fullSize: 0
    }
  },
  computed: {
    activeUser () {
      return this.$store.getters.userInfo || this.$store.getters.activeUser
    },
    newMessage: {
      get: function () {
        return this.$store.getters.newMessage
      },
      set: function (data) {
        this.$store.dispatch('setNewMessage', data)
      }
    }
  },
  methods: {
    downloadFile (e, file) {
      if (!file.base64 || !this.disabled) return
      e.target.href = file.base64
      delay(this.removeHref, 10, e.target)
    },
    removeHref (target) {
      target.href = ''
    },
    uploadFile (file) {
      this.$emit('startLoad')
      const fileData = new FormData()
      fileData.set('label', file.name)
      fileData.set('file', file)
      fileData.set('fieldname', 'uploadFile')
      UploadApi.uploadFile(fileData)
        .then(resp => {
          this.$emit('endLoad')
          const index = findIndex(this.files, {id: file.id})
          if (index !== -1) {
            this.$set(file, 'id', resp.data.id)
            this.$set(file, 'uploaded', true)
            this.$set(this.files, index, file)
          }
        })
        .catch(err => {
          console.error(err)
        })
    },
    deleteUploadFile (fileId) {
      UploadApi.deleteFile(fileId)
    },
    getBase64 (file) {
      if (!file.base64) return
      const index = +/base64,/.test(file.base64)
      return file.base64.split('base64,')[index]
    },
    isImage (file) {
      return /image/gmi.test(file.type) && file.base64
    },
    deleteFile (e, index) {
      e.preventDefault()
      const file = this.files[index] || {}
      this.fullSize -= file.size || 0
      if (file.id && this.spaces) this.deleteUploadFile(file.id)
      this.files.splice(index, 1)
    },
    setError (msg) {
      this.error = msg || ''
    },
    getFileInput (e) {
      const files = e ? e.target.files : this.dropped
      const lengthFiles = this.files.length
      const maximumSize = 2.5e+7 // 25MB
      const sizeMb = maximumSize / 1e+6
      forEach(files, (file, index) => {
        const expectedSize = this.fullSize + file.size
        if (file.size > maximumSize || expectedSize > maximumSize) {
          this.setError(`Maximum attachment file sizes: ${sizeMb}MB`)
          delay(this.setError, 1500)
          return
        }
        this.fullSize += file.size
        file.id = uniqueId()
        this.files.push(file)
        this.setBase64(file, index + lengthFiles)
      })
      e.target.value = ''
    },
    setStatusUpload (file, index) {
      file.status = true
      this.$set(this.files, index, file)
    },
    setBase64 (file, index) {
      if (this.spaces) {
        this.uploadFile(file)
      }
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onprogress = (e) => {
        const percent = (e.loaded * 100) / e.total
        if (percent === 100) {
          delay(this.setStatusUpload, 500, file, index)
        }
      }
      reader.onload = () => {
        if (!this.spaces) this.$set(file, 'uploaded', true)
        this.$set(file, 'base64', reader.result)
        this.$set(this.files, index, file)
      }
    }
  },
  watch: {
    dropped () {
      this.getFileInput()
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../../sass/variables";
  @import "../../../sass/mixins";

  .bbe-upload-files {
    position: relative;
    display: grid;
    grid-template-columns: auto 25px;
    width: 100%;
    margin: 0;
    margin-left: -8px;

    &:after,
    &:before {
      display: none;
    }

    &__error {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      bottom: 100%;
      justify-self: center;
      width: 100%;
      color: $color-pomegranate;
      z-index: 1;
    }

    &-list {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      max-height: 55px;

      &__transition {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        width: 100%;
      }

      &__container {
        padding: 8px 20px 8px 8px;
      }

      &__name {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
      }

      &__close {
        position: absolute;
        top: 1px;
        right: 1px;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 20px;
        height: 20px;
        font-size: 8px;
        color: $color-silver-chalice;
        transition: .2s;

        &:hover {
          opacity: 0.8;
        }
      }
    }

    &-button {
      &__input {
        width: 0.1px;
        height: 0.1px;
        opacity: 0;
        overflow: hidden;
        position: absolute;
        z-index: -1;
      }

      &__icon {
        color: $color-silver-chalice;
        font-size: 25px;
        margin-right: 9px;
        transition: .2s ease-in;
        cursor: pointer;

        &:hover {
          opacity: 0.7;
        }

        &:before {
          margin: 0;
        }
      }
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/Main/UploadFiles/UploadFiles.vue